import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn.ts';

const scrollAreaVariants = cva('min-h-0 flex-1 overflow-y-auto', {
    variants: {
        scrollbar: {
            default: '',
            custom: 'custom-scrollbar',
            hidden: 'scrollbar-hide',
        },
        padding: {
            none: '',
            sm: 'p-2',
            md: 'p-4',
            lg: 'p-6',
        },
    },
    defaultVariants: {
        scrollbar: 'default',
        padding: 'none',
    },
});

export interface ScrollAreaProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof scrollAreaVariants> {}

/**
 * ScrollArea - A flex-friendly scrollable container
 * 
 * Abstracts the `flex-1 overflow-y-auto min-h-0` pattern required for
 * proper scrolling in flexbox layouts. Use inside any flex container
 * where you need scrollable content.
 * 
 * @example
 * <div className="flex flex-col h-full">
 *   <header>Fixed Header</header>
 *   <ScrollArea padding="md">
 *     {longContent}
 *   </ScrollArea>
 * </div>
 */
export const ScrollArea: React.FC<ScrollAreaProps> = ({
    children,
    className,
    scrollbar,
    padding,
    ...props
}) => (
        <div 
            className={cn(scrollAreaVariants({ scrollbar, padding }), className)}
            {...props}
        >
            {children}
        </div>
    );
